import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { useUser } from "../context/UserContext";

function getRoleNames(user) {
  const roleSet = new Set();
  if (user?.rol) roleSet.add(String(user.rol).trim().toLowerCase());
  if (user?.role) roleSet.add(String(user.role).trim().toLowerCase());
  for (const role of user?.roles || []) {
    if (!role) continue;
    if (typeof role === "string") {
      roleSet.add(String(role).trim().toLowerCase());
      continue;
    }
    if (typeof role === "object") {
      const name = role.name || role.naam;
      if (name) roleSet.add(String(name).trim().toLowerCase());
    }
  }
  return [...roleSet];
}

function getDefaultRoute(user) {
  const roles = getRoleNames(user);
  if (roles.includes("platformadmin")) return "/admin/dashboard";
  if (roles.includes("zzp_owner")) return "/pl";
  if (roles.includes("monteur")) return "/monteur/werkbonnen";
  if (roles.includes("tenantadmin")) return "/pl";
  if (roles.includes("controller")) return "/pl";
  return "/dashboard";
}

export default function ForcePasswordResetPage() {
  const navigate = useNavigate();
  const { user, login, logout } = useUser();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function handleSubmit(event) {
    event.preventDefault();
    if (loading) return;

    setError("");
    setSuccess("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("Vul alle velden in.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Nieuw wachtwoord en herhaling zijn niet gelijk.");
      return;
    }

    if (newPassword.length < 10) {
      setError("Nieuw wachtwoord moet minimaal 10 tekens bevatten.");
      return;
    }

    try {
      setLoading(true);
      const res = await api.post("/auth/change-password", {
        currentPassword,
        newPassword,
        logoutOtherSessions: true,
      });

      const token = String(res?.data?.accessToken || "").trim();
      if (token) {
        login(token, String(res?.data?.refreshToken || "").trim());
      }

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSuccess("Wachtwoord gewijzigd. Je wordt doorgestuurd.");

      navigate(getDefaultRoute(user), { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || "Wachtwoord wijzigen mislukt");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10">
      <div className="mx-auto max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
          Beveiliging
        </p>
        <h1 className="mt-3 text-3xl font-bold text-slate-900">
          Wachtwoord direct wijzigen
        </h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          Voor dit account is een verplichte wachtwoordreset geactiveerd. Je krijgt
          pas weer toegang tot de rest van de applicatie nadat je een nieuw
          wachtwoord hebt ingesteld.
        </p>

        <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Huidig wachtwoord
            </span>
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              required
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Nieuw wachtwoord
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              required
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Herhaal nieuw wachtwoord
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              required
            />
          </label>

          {error ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          ) : null}

          {success ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {success}
            </div>
          ) : null}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {loading ? "Bezig..." : "Nieuw wachtwoord opslaan"}
            </button>
            <button
              type="button"
              onClick={logout}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
            >
              Uitloggen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
